import { useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { AddImageModal } from '@components/add-image/add-image';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { Middle } from '@components/form-builder/content';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconReplace } from '@components/icons/replace';
import { InfoBox } from '@components/info-box';
import { Spinner } from '@components/loading-spinner';
import { useModal } from '@components/modal';
import { PageContainer } from '@components/page-containers';
import { PortalCard } from '@components/portal-card';
import { SelectMulti } from '@components/selectmulti';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  usePortalsUsingJourneyForm,
  useUpdateJourneyFormPortalInfo,
} from 'api/journey-forms/hooks';
import { JourneyForm } from 'api/journey-forms/requests';
import {
  useAddJourneyFormToPortal,
  usePortals,
  useRemoveJourneyFormFromPortal,
} from 'api/portals/hooks';

export const JourneyFormSettingsPage = ({
  form,
  isAutoStartOnChange,
  portalOnChange,
}: {
  form: JourneyForm;
  isAutoStartOnChange: (isAutoStart: boolean) => void;
  portalOnChange: (isPortalForm: boolean) => void;
}) => {
  return (
    <Container>
      <PageContainer.Page>
        <PageContainer.PageContent>
          <Middle>
            <Stack space={6}>
              <FormSubmission isAutoStart={form.isAutoStart} onChange={isAutoStartOnChange} />
              <Separator />
              <Portal form={form} onChange={portalOnChange} />
            </Stack>
          </Middle>
        </PageContainer.PageContent>
      </PageContainer.Page>
    </Container>
  );
};

const FormSubmission = ({
  isAutoStart,
  onChange,
}: {
  isAutoStart?: JourneyForm['isAutoStart'];
  onChange: (isAutoStart: boolean) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Stack space={2}>
        <Text text={t('formSubmission')} font="bold18" />
        <Text text={t('formSubmissionInfo')} font="regular14" color="textSecondary" />
      </Stack>
      <Stack space={2} orientation="horizontal" align="center">
        <Switch
          label={t('autoSwitchTitle')}
          defaultValue={Boolean(isAutoStart)}
          labelVisible={false}
          onChange={onChange}
        />
        <Text text={t('autoSwitchTitle')} font="regular12" />
      </Stack>
    </Stack>
  );
};

type PortalFormProps = {
  formTitle: string;
  formPortalDescription: string;
};

const Form = createForm<PortalFormProps>();

const Portal = ({
  form,
  onChange,
}: {
  form: JourneyForm;
  onChange: (isPortalForm: boolean) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { data: portals, isLoading: isPortalsLoading } = usePortals();
  const { data: portalsInUseOptions, isLoading: isPortalsInUseLoading } =
    usePortalsUsingJourneyForm(form?.shortId ?? '');

  const addJourneyFormToPortal = useAddJourneyFormToPortal();
  const removeJourneyFormFromPortal = useRemoveJourneyFormFromPortal();

  const [image, setImage] = useState<File | undefined>(undefined);
  const updatePortalInfoMutation = useUpdateJourneyFormPortalInfo({
    journeyShortId: form?.journey ?? '',
    journeyFormId: form?.shortId ?? '',
    onSuccess: () => {
      setImage(undefined);
    },
  });
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isDirty = formStore.useIsDirty();
  const formTitle = formValues['formTitle'] as string;
  const formDescription = formValues['formPortalDescription'] as string;
  const showModal = useModal(AddImageModal, { variant: 'LARGE' });

  const [selectedPortals, setSelectedPortals] = useState<any[]>([]);

  useEffect(() => {
    if (Array.isArray(portalsInUseOptions)) {
      setSelectedPortals(portalsInUseOptions);
    }
  }, [portalsInUseOptions]);

  // Reset the form store, if the form name changes
  // because that means the user updated the name in the corner
  useEffect(() => {
    formStore.actions.resetStore();
    setImage(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name]);

  const handleSubmit = async (values: PortalFormProps) => {
    const { formTitle, formPortalDescription } = values;
    const data = {
      name: formTitle ?? form.name,
      portalDescription: formPortalDescription,
      ...(image && { portalImage: image }),
    };
    await updatePortalInfoMutation.mutateAsync(data);
  };

  const handlePortalSelectionChange = async (selectedOptions: any[]) => {
    const newSelection = selectedOptions.map((option) => option.value);
    const currentSelection = selectedPortals.map((p) => p.value);

    const addedPortals = newSelection.filter((portalId) => !currentSelection.includes(portalId));
    const removedPortals = currentSelection.filter((portalId) => !newSelection.includes(portalId));

    setSelectedPortals(selectedOptions);

    for (const portalId of addedPortals) {
      await addJourneyFormToPortal.mutateAsync({
        portalShortId: portalId,
        form: form.shortId,
      });
    }

    for (const portalId of removedPortals) {
      await removeJourneyFormFromPortal.mutateAsync({
        portalShortId: portalId,
        formShortId: form.shortId,
        useJourneyFormId: true,
      });
    }
  };

  const isEditing = isDirty || image !== undefined;

  const portalImage = image ? URL.createObjectURL(image) : form?.portalImageUrl;

  const portalOptions =
    portals?.map((portal) => ({
      label: portal.name,
      value: portal.shortId,
    })) ?? [];

  if (isPortalsLoading || isPortalsInUseLoading) {
    return <Spinner />;
  }

  return (
    <Form onSubmit={handleSubmit} store={formStore}>
      <Stack space={6} style={{ width: pxToRem(680) }}>
        <Stack space={2}>
          <Text text={t('portal')} font="bold18" />
          <SelectMulti
            label={t('showOnPortals')}
            options={portalOptions}
            value={selectedPortals}
            onChange={handlePortalSelectionChange}
          />
        </Stack>

        {!form?.isPublished && (
          <InfoBox
            variant="indigo"
            text={t('infoBoxText')}
            icon={<IconFormIndicatorInfo color="indigo100" />}
          />
        )}

        <Card variant="transparent" space={6}>
          <Stack space={6} orientation="horizontal" justify="space-between">
            <Stack space={4} style={{ width: '100%' }}>
              <Form.TextField
                id="formTitle"
                label={t('formTitle')}
                validators={['required']}
                defaultValue={form.name}
              />
              <Form.TextField
                id="formPortalDescription"
                label={t('description')}
                defaultValue={form.portalDescription ?? form.description}
                placeholder={t('descrptionPlaceholderText')}
              />
              <Button
                variant="secondary"
                onClick={() => showModal({ onChange: setImage })}
                text={t('replaceImage')}
                iconLeft={<IconReplace />}
              />
              {isEditing && (
                <Form.Actions>
                  <AnimatePresence>
                    <AnimatedDiv>
                      <Stack orientation="horizontal" space={4} justify="flex-end">
                        <Button
                          text={t('core.cancel')}
                          variant="secondary"
                          onClick={() => {
                            formStore.actions.resetStore();
                            setImage(undefined);
                          }}
                        />
                        <Form.Submit text={t('core.save')} variant="primary-blue" />
                      </Stack>
                    </AnimatedDiv>
                  </AnimatePresence>
                </Form.Actions>
              )}
            </Stack>
            <div style={{ width: '100%' }}>
              <PortalCard name={formTitle} description={formDescription} image={portalImage} />
            </div>
          </Stack>
        </Card>
      </Stack>
    </Form>
  );
};

const AnimatedDiv = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      transition={{ duration: 0.2 }}
      animate={{ opacity: 1, height: 'auto' }}
      initial={{ opacity: 0, height: 0 }}
      exit={{ opacity: 0, height: 0 }}
    >
      {children}
    </motion.div>
  );
};

const Container = styled.div({
  background: theme.color.backgroundPrimary,
});

const locale = {
  en: {
    formSubmission: 'Form submission',
    formSubmissionInfo:
      'When enabled, the journey workflow will start automatically on form submission',
    autoSwitchTitle: 'Start journey on form submission',
    portal: 'Portal',
    replaceImage: 'Replace image',
    formTitle: 'Form title',
    description: 'Portal Description',
    descrptionPlaceholderText: 'Description for this form',
    infoBoxText:
      'This form is currently in-active, re-activate this form to display it on the Portal',
    showOnPortals: 'Show on portals',
  },
};
